import React, { useState, useEffect } from "react";
import {
  Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, CircularProgress, Select, MenuItem, FormControl, InputLabel, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, List, ListItem, ListItemText,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  getAllCourtAssignments,
  addBallkidComment,
  getBallkidComments,
  getBallkids,
  getCourtStartTime,
} from "../firebaseService";
import { useTournamentConfig } from "../context/TournamentConfigContext";
import { useAuth } from "../AuthContext";
import { formatDayWithWeekday } from "../utils/dateLabel";
import { useDefaultTournamentDay } from "../utils/useDefaultTournamentDay";
import './printStyle.css';

const courts = ["Center Court", "Court 1", "Court 2", "Court 3", "Court 4"];

const CourtAssignmentsPrintPage = () => {
  const { daysOfWeek } = useTournamentConfig();
  const { user } = useAuth();

  const [selectedDay, setSelectedDay] = useDefaultTournamentDay(daysOfWeek);
  const [courtAssignments, setCourtAssignments] = useState({});
  const [courtStartTimes, setCourtStartTimes] = useState({}); // court -> { startTime, hasThreeCrews, isTurnusEnabled }
  const [loading, setLoading] = useState(true);
  const [nameToId, setNameToId] = useState({});
  const [exporting, setExporting] = useState(false);

  // Comment dialog state
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedName, setSelectedName] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [commentsLoading, setCommentsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [assignments, allBallkids, ...startTimes] = await Promise.all([
          getAllCourtAssignments(selectedDay),
          getBallkids().catch(() => []),
          ...courts.map((court) =>
            getCourtStartTime(selectedDay, court).catch(() => ({}))
          ),
        ]);
        if (assignments) setCourtAssignments(assignments);

        const timesMap = {};
        courts.forEach((court, idx) => {
          timesMap[court] = startTimes[idx] || {};
        });
        setCourtStartTimes(timesMap);

        // Build name→id map for comment lookup
        const map = {};
        allBallkids.forEach((bk) => { map[bk.name] = bk.id; });
        setNameToId(map);

        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedDay]);

  const handleNameClick = async (name) => {
    const id = nameToId[name];
    if (!id) return;
    setSelectedName(name);
    setSelectedId(id);
    setNewComment("");
    setCommentDialogOpen(true);
    setCommentsLoading(true);
    try {
      const loaded = await getBallkidComments(id);
      setComments(loaded);
    } catch (e) {
      setComments([]);
    }
    setCommentsLoading(false);
  };

  const buildTimeslots = (startTime, isTurnusEnabled) => {
    if (!startTime) return [];
    const [h, m] = startTime.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return [];
    const interval = isTurnusEnabled ? 45 : 60;
    const endHour = 20;
    const slots = [];
    const cur = new Date();
    cur.setHours(h, m, 0, 0);
    const end = new Date();
    end.setHours(endHour, 0, 0, 0);
    while (cur <= end) {
      slots.push(
        `${String(cur.getHours()).padStart(2, "0")}:${String(cur.getMinutes()).padStart(2, "0")}`
      );
      cur.setMinutes(cur.getMinutes() + interval);
    }
    return slots;
  };

  const handleExportPdf = async () => {
    try {
      setExporting(true);
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const marginX = 12;
      const dayLabel = formatDayWithWeekday(selectedDay);

      // ---- Titel ----
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(`Platzeinteilungen - ${dayLabel}`, pageWidth / 2, 15, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(
        `Erstellt am ${new Date().toLocaleString("de-DE")}`,
        pageWidth / 2,
        21,
        { align: "center" }
      );

      let cursorY = 28;

      // ---- Einteilungen pro Court ----
      const hasAnyAssignment = courts.some((court) =>
        ["crewA", "crewB", "crewC"].some(
          (crew) => (courtAssignments[`${selectedDay}_${court}_${crew}`] || []).length > 0
        )
      );

      if (!hasAnyAssignment) {
        doc.setFontSize(11);
        doc.text("Keine Einteilungen fuer diesen Tag.", marginX, cursorY);
        cursorY += 8;
      }

      courts.forEach((court) => {
        const crewKeys = ["crewA", "crewB", "crewC"].filter(
          (c) => (courtAssignments[`${selectedDay}_${court}_${c}`] || []).length > 0
        );
        if (crewKeys.length === 0) return;

        const startInfo = courtStartTimes[court] || {};
        const subtitle = startInfo.startTime
          ? ` (Start ${startInfo.startTime}${startInfo.isTurnusEnabled ? ", 45 min Turnus" : ""})`
          : "";

        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.text(`${court}${subtitle}`, marginX, cursorY);
        cursorY += 2;

        const maxRows = Math.max(
          ...crewKeys.map(
            (c) => (courtAssignments[`${selectedDay}_${court}_${c}`] || []).length
          )
        );
        const head = [["#", ...crewKeys.map((c) => `Crew ${c.replace("crew", "")}`)]];
        const body = [];
        for (let i = 0; i < maxRows; i += 1) {
          const row = [String(i + 1)];
          crewKeys.forEach((c) => {
            const list = courtAssignments[`${selectedDay}_${court}_${c}`] || [];
            row.push(list[i] || "");
          });
          body.push(row);
        }

        autoTable(doc, {
          head,
          body,
          startY: cursorY + 2,
          margin: { left: marginX, right: marginX },
          styles: { fontSize: 10, cellPadding: 2, overflow: "linebreak" },
          headStyles: { fillColor: [33, 64, 114], textColor: 255, halign: "center" },
          bodyStyles: { valign: "middle" },
          columnStyles: { 0: { halign: "center", cellWidth: 10 } },
          theme: "grid",
        });

        cursorY = doc.lastAutoTable.finalY + 6;
      });

      // ---- Crew-Startzeiten Seite ----
      const courtsWithTimes = courts.filter((c) => courtStartTimes[c]?.startTime);
      if (courtsWithTimes.length > 0) {
        doc.addPage();
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text(`Crew-Startzeiten - ${dayLabel}`, pageWidth / 2, 15, { align: "center" });
        cursorY = 24;

        courtsWithTimes.forEach((court) => {
          const info = courtStartTimes[court];
          const timeslots = buildTimeslots(info.startTime, info.isTurnusEnabled);
          if (timeslots.length === 0) return;

          const crewLabels = info.hasThreeCrews ? ["A", "B", "C"] : ["A", "B"];
          const rotation = crewLabels.length;
          const rows = crewLabels.map((label, crewIdx) => {
            const cells = timeslots.map((_, slotIdx) =>
              slotIdx % rotation === crewIdx ? court : "Pause"
            );
            return [`Crew ${label}`, ...cells];
          });

          doc.setFont("helvetica", "bold");
          doc.setFontSize(12);
          doc.text(
            `${court} - Start ${info.startTime}${info.isTurnusEnabled ? ", 45 min Turnus" : ", 60 min Turnus"}`,
            marginX,
            cursorY
          );

          autoTable(doc, {
            head: [["Crew", ...timeslots]],
            body: rows,
            startY: cursorY + 2,
            margin: { left: marginX, right: marginX },
            styles: { fontSize: 8, cellPadding: 1.5, overflow: "linebreak", halign: "center" },
            headStyles: { fillColor: [33, 64, 114], textColor: 255 },
            columnStyles: { 0: { fontStyle: "bold", halign: "left", cellWidth: 18 } },
            theme: "grid",
            tableWidth: "auto",
            didParseCell: (data) => {
              if (data.section === "body" && data.column.index > 0) {
                if (data.cell.raw === "Pause") {
                  data.cell.styles.textColor = [150, 150, 150];
                } else {
                  data.cell.styles.fillColor = [227, 242, 253];
                  data.cell.styles.fontStyle = "bold";
                }
              }
            },
          });

          cursorY = doc.lastAutoTable.finalY + 8;
        });
      }

      // ---- Seitenzahlen ----
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      for (let i = 1; i <= pageCount; i += 1) {
        doc.setPage(i);
        doc.text(
          `Seite ${i} / ${pageCount}`,
          pageWidth - marginX,
          doc.internal.pageSize.getHeight() - 6,
          { align: "right" }
        );
      }

      doc.save(`Einteilungen_${selectedDay.replace(/\s+/g, "_")}.pdf`);
    } catch (err) {
      console.error("Fehler beim PDF-Export:", err);
    } finally {
      setExporting(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedId) return;
    const comment = {
      text: newComment.trim(),
      timestamp: new Date().toISOString(),
      userEmail: user?.email || "Unbekannt",
    };
    await addBallkidComment(selectedId, comment);
    setComments([...comments, comment]);
    setNewComment("");
  };

  return (
    <Box>
        <Typography variant="h5" sx={{ textAlign: "center", mb: 2 }}>
          Platzeinteilungen fuer {selectedDay}
        </Typography>

        <FormControl fullWidth style={{ marginBottom: "20px" }}>
          <InputLabel id="select-day-label">Tag auswaehlen</InputLabel>
          <Select
            labelId="select-day-label"
            value={selectedDay}
            onChange={(e) => setSelectedDay(e.target.value)}
            sx={{
              '.MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
              '.MuiSvgIcon-root': { color: 'white' },
            }}
          >
            {daysOfWeek.map((day) => (
              <MenuItem key={day} value={day}>{day}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <div>
          {loading ? (
            <CircularProgress style={{ marginTop: 20 }} />
          ) : (
            courts.map((court) =>
              ["crewA", "crewB", "crewC"].map((crew) => {
                const key = `${selectedDay}_${court}_${crew}`;
                if (!courtAssignments[key] || courtAssignments[key].length === 0) return null;

                return (
                  <div key={`${court}-${crew}`} style={{ marginBottom: '20px' }}>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {court} - Crew {crew.replace("crew", "")}
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Pos</TableCell>
                            <TableCell>Name</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {courtAssignments[key].map((name, index) => (
                            <TableRow
                              key={index}
                              hover
                              sx={{ cursor: nameToId[name] ? "pointer" : "default" }}
                              onClick={() => handleNameClick(name)}
                            >
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{name || "Unbekannt"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                );
              })
            )
          )}
        </div>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPdf}
            disabled={loading || exporting}
          >
            {exporting ? "Erstelle PDF..." : "Als PDF exportieren"}
          </Button>
          <Button
            variant="outlined"
            onClick={() => window.print()}
            disabled={loading}
          >
            Browser drucken
          </Button>
        </Box>

        {/* Comment Dialog */}
        <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Kommentare - {selectedName}</DialogTitle>
          <DialogContent>
            {commentsLoading ? (
              <CircularProgress size={24} />
            ) : comments.length > 0 ? (
              <List dense>
                {comments.map((c, i) => (
                  <ListItem key={i} sx={{ flexDirection: "column", alignItems: "flex-start" }}>
                    <ListItemText
                      primary={c.text}
                      secondary={`${new Date(c.timestamp).toLocaleString("de-DE")} - ${c.userEmail || "Unbekannt"}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="text.secondary" sx={{ py: 2 }}>Keine Kommentare vorhanden.</Typography>
            )}
            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Neuer Kommentar..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
              />
              <Button variant="contained" onClick={handleAddComment} disabled={!newComment.trim()}>
                Senden
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCommentDialogOpen(false)}>Schliessen</Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
};

export default CourtAssignmentsPrintPage;
